import { atom } from 'recoil';
import type { SaveReport } from '~/domain/services/save/SaveOutcome';

/**
 * Monotonic counter requesting an automatic save-all of pending
 * structure-set changes. Bumped by flows where leaving the change
 * unpersisted is a safety concern — today: a user-confirmed approval
 * transition (approve / reject / unapprove), mirroring Eclipse's
 * save-on-approval behavior, so a clinical sign-off never exists only in
 * browser memory.
 *
 * Consumed by {@link SaveStatusIndicator}, whose save instance owns the
 * result UI (snackbars, conflict dialog) — the requester never runs the
 * save itself, so conflicts always surface through the one dialog.
 */
export const saveRequestedState = atom<number>({
    key: 'saveRequested',
    default: 0,
});

/**
 * Whether a dataset save is in flight — shared by every
 * {@link useSavePatientChanges} instance so concurrent call sites (the
 * header indicator, save-and-close in the patient menu) see one save,
 * not per-instance copies.
 */
export const patientSavingState = atom<boolean>({
    key: 'patientSaving',
    default: false,
});

/**
 * Whether the in-flight save is retrying a request after a transient
 * network failure. Shared for the same reason as
 * {@link patientSavingState}; only meaningful while that is true.
 */
export const patientSaveReconnectingState = atom<boolean>({
    key: 'patientSaveReconnecting',
    default: false,
});

/**
 * The last save's report, cleared when a new save starts. Shared for the
 * same reason as {@link patientSavingState}: whichever call site ran
 * the save, its failures and conflicts must surface in the ONE conflict
 * dialog (rendered by SaveStatusIndicator) — a save triggered from the
 * patient menu must never have its conflicts vanish into a snackbar.
 *
 * Snapshots inside the report are immutable and the report is replaced
 * wholesale, so dangerouslyAllowMutability is not needed.
 */
export const saveReportState = atom<SaveReport | null>({
    key: 'patientSaveReport',
    default: null,
});

/** A save the user is asked to confirm before an action continues. */
export type SaveOffer = {
    readonly title: string;
    /** Why the save is needed, shown above the pending changes. */
    readonly message: string;
    /** The action that saves and then continues, e.g. "Save and start". */
    readonly saveLabel: string;
    /** True when the user confirmed and the save stored every change. */
    readonly resolve: (saved: boolean) => void;
};

/**
 * The open save offer. Consumed by {@link SaveStatusIndicator}, which shows
 * it in the review panel and runs the save through the one save instance
 * that owns the result UI.
 */
export const saveOfferState = atom<SaveOffer | null>({
    key: 'saveOffer',
    default: null,
});
